import { tone } from "@/components/PredictionScore";

// Large glowing semicircle gauge matching the EV-IDEX reference art. Same inputs as PredictionScore's Arc (score 0–100 or
// empty); only the rendering is new. Empty = cyan glowing track + "Insufficient Prediction Data"; scored = tone-coloured fill.
const CX = 180, CY = 170, R = 118;
const pt = (v, r) => { const a = Math.PI * (1 - v / 100); return [CX + r * Math.cos(a), CY - r * Math.sin(a)]; };
const MAJOR = [0, 25, 50, 75, 100];
const MINOR = Array.from({ length: 21 }, (_, i) => i * 5).filter((v) => v % 25 !== 0);

export const HeroGauge = ({ score, empty, tid, mini = false, className = "" }) => {
  const c = Math.PI * R, f = empty || score == null ? 0 : Math.max(0, Math.min(100, score)) / 100;
  const fill = empty ? "#22d3ee" : tone(score);
  const id = `${tid}-glow`;
  return (
    <svg viewBox="0 0 360 214" className={className} data-testid={`${tid}-gauge`} role="img" aria-label={empty ? (mini ? "Insufficient data" : "Score not published yet") : `Score ${score} out of 100`}>
      <defs>
        <filter id={id} x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation={mini ? 2 : 5} result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        <linearGradient id={`${id}-track`} x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#0891b2" /><stop offset="0.5" stopColor="#67e8f9" /><stop offset="1" stopColor="#0891b2" /></linearGradient>
      </defs>
      <path d={`M${pt(0, R)} A${R} ${R} 0 0 1 ${pt(100, R)}`} fill="none" stroke={empty ? `url(#${id}-track)` : "#1E293B"} strokeWidth={mini ? 16 : 20} strokeLinecap="round"
        style={{ filter: empty ? `url(#${id})` : "none", opacity: empty ? 0.9 : 1 }} />
      {!empty && (
        <path d={`M${pt(0, R)} A${R} ${R} 0 0 1 ${pt(100, R)}`} fill="none" stroke={fill} strokeWidth={mini ? 16 : 20} strokeLinecap="round"
          strokeDasharray={`${c} ${c}`} strokeDashoffset={c * (1 - f)} style={{ transition: "stroke-dashoffset 900ms ease", filter: `url(#${id})` }} />
      )}
      {!mini && MINOR.map((v) => { const [x1, y1] = pt(v, R + 16), [x2, y2] = pt(v, R + 21); return <line key={v} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#155e75" strokeWidth="1.5" />; })}
      {MAJOR.map((v) => {
        const [x1, y1] = pt(v, R - 14), [x2, y2] = pt(v, R + (v === 0 || v === 100 ? 2 : 24)), [tx, ty] = pt(v, R + (v === 0 || v === 100 ? 44 : 38));
        return (
          <g key={v}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#0B0F17" strokeWidth={v === 0 || v === 100 ? 0 : 3.5} strokeLinecap="round" />
            {(v === 0 || v === 100) && <line x1={pt(v, R - 22)[0]} y1={y1} x2={pt(v, R + 22)[0]} y2={y1} stroke="#94a3b8" strokeWidth="3" strokeLinecap="round" />}
            {!mini && <text x={tx} y={ty + (v === 50 ? 0 : 6)} textAnchor="middle" fill="#cbd5e1" fontSize="16" fontWeight="600" className="font-mono-ticker">{v}</text>}
          </g>
        );
      })}
      {empty ? (
        <g>
          <g fill="#64748b" opacity="0.9" transform={`translate(${CX - 20} ${mini ? 112 : 84})`}><rect x="0" y="18" width="9" height="18" rx="1.5" /><rect x="15" y="9" width="9" height="27" rx="1.5" /><rect x="30" y="0" width="9" height="36" rx="1.5" /></g>
          {!mini && <text x={CX} y="148" textAnchor="middle" fill="#FCD34D" fontSize="24" fontWeight="700" style={{ filter: `url(#${id})` }} data-testid={`${tid}-score`}>
            <tspan x={CX} dy="0">Score not</tspan><tspan x={CX} dy="27">published yet</tspan>
          </text>}
          {mini && <text x={CX} y="178" textAnchor="middle" fill="#FCD34D" fontSize="18" fontWeight="700" data-testid={`${tid}-score`}>—</text>}
        </g>
      ) : (
        <g>
          <text x={CX} y="152" textAnchor="middle" fill="#F8FAFC" fontSize={mini ? 44 : 56} fontWeight="700" className="font-mono-ticker" data-testid={`${tid}-score`}>{score}</text>
          <text x={CX} y="178" textAnchor="middle" fill="#64748b" fontSize="13">/ 100</text>
        </g>
      )}
    </svg>
  );
};
